import { OnboardingLayout } from "@layouts/OnboardingLayout";
import { registerDeveloper } from "@services/krentDeveloper";
import { PrimaryButton } from "@shared/buttons";
import { InputComponent } from "@shared/forms";
import ImageUpload from "@shared/image-upload/ImageUpload";
import Logo from "@shared/Logo";
import {
  ErrorDisplay,
  SuccessDisplay,
  WarningDisplay,
} from "@shared/notifications";
import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

interface RegistrationState {
  firstname: string;
  lastname: string;
  email: string;
  preferredName: string;
  phone: string;
  password: string;
  passwordConfirm: string;
  role: string;
  socialMediaSource: string;
  niesvNumber?: string;
  aeanNumber?: string;
}

const DeveloperUpload = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [taxId, setTaxId] = useState("");
  const [websiteLink, setWebsiteLink] = useState("");
  const [details, setDetails] = useState("");
  const [agree, setAgree] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState<{ message: string; field: string }[]>([]);
  const [companyName, setCompanyName] = useState("");
  const [companyAddress, setCompanyAddress] = useState("");
  const [loading, setLoading] = useState<boolean>(false);
  const [profilePicture, setProfilePicture] = useState<File | null>(null);

  const stateFromLocation = location.state as RegistrationState | null;
  const stateFromStorage = localStorage.getItem("krent-signup-developer-data");
  let state: RegistrationState | null = null;

  try {
    state =
      stateFromLocation ||
      (stateFromStorage ? JSON.parse(stateFromStorage) : null);
  } catch (error) {
    console.error("Error parsing stored developer data:", error);
    localStorage.removeItem("krent-signup-developer-data");
  }

  const { firstname = "", lastname = "", email = "" } = state || {};
  console.log("DeveloperUpload state:", state);
  const handleImageUpload = (file: File): void => {
    setProfilePicture(file);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError([]);

    if (!state) {
      setError([
        {
          message:
            "We couldn't find your registration information. Please go back to the previous step and try again.",
          field: "general",
        },
      ]);
      setLoading(false);
      return;
    }

    if (!agree) {
      setError([
        {
          message: "Please accept the terms and conditions to continue",
          field: "terms",
        },
      ]);
      setLoading(false);
      return;
    }

    const formData = new FormData();

    // Add all the registration data from the first step
    Object.entries(state).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        formData.append(key, value.toString());
      }
    });

    // Add the additional developer-specific fields
    if (taxId) formData.append("taxId", taxId);
    if (websiteLink) formData.append("websiteLink", websiteLink);
    if (details) formData.append("details", details);
    if (companyName) formData.append("companyName", companyName);
    if (companyAddress) formData.append("companyAddress", companyAddress);

    if (profilePicture) {
      formData.append("photo", profilePicture);
    }

    try {
      console.log(
        "Submitting form data:",
        Object.fromEntries(formData.entries())
      );
      await registerDeveloper(formData);
      setSuccess("Account created successfully!");
      // Clear the stored data
      localStorage.removeItem("krent-signup-developer-data");
      navigate("/redirect", { state: { firstname, lastname, email } });
    } catch (err: any) {
      console.log("Registration error:", err);
      if (Array.isArray(err?.errors)) {
        // Format the errors properly
        const formattedErrors = err.errors.map((error: any) => ({
          message: error.message || error,
          field: error.field || "general",
        }));
        setError(formattedErrors);
      } else if (err?.message) {
        // Handle specific error messages
        let field = "general";
        let message = err.message;

        if (message.toLowerCase().includes("email already in use")) {
          field = "email";
          message =
            "This email is already registered. Please use a different email address or sign in with your existing account.";
        } else if (message.toLowerCase().includes("phone")) {
          field = "phone";
          message =
            "Please enter a valid phone number (11 digits starting with 0).";
        } else if (message.toLowerCase().includes("password")) {
          field = "password";
          message =
            "Password must be at least 8 characters long. Please choose a stronger password.";
        } else if (
          message.toLowerCase().includes("username") ||
          message.toLowerCase().includes("preferredname")
        ) {
          field = "username";
          message =
            "Username must be unique and different from your first and last name.";
        } else if (message.toLowerCase().includes("required")) {
          message = "Please fill in all required fields marked with *.";
        } else if (message.toLowerCase().includes("invalid")) {
          message = "Please check your input and try again.";
        } else if (
          message.toLowerCase().includes("tax") ||
          message.toLowerCase().includes("taxid")
        ) {
          field = "taxId";
          message = "Please enter a valid tax identification number.";
        } else if (
          message.toLowerCase().includes("website") ||
          message.toLowerCase().includes("websiteLink")
        ) {
          field = "websiteLink";
          message =
            "Please enter a valid website URL (e.g., https://example.com).";
        } else if (
          message.toLowerCase().includes("company") ||
          message.toLowerCase().includes("companyName")
        ) {
          field = "companyName";
          message = "Please enter your company name.";
        } else if (
          message.toLowerCase().includes("address") ||
          message.toLowerCase().includes("companyAddress")
        ) {
          field = "companyAddress";
          message = "Please enter your company address.";
        } else if (
          message.toLowerCase().includes("details") ||
          message.toLowerCase().includes("bio")
        ) {
          field = "details";
          message = "Please provide company details and history.";
        }

        setError([{ message, field }]);
      } else {
        setError([
          {
            message:
              "Something went wrong. Please check your information and try again.",
            field: "general",
          },
        ]);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <OnboardingLayout>
      <div className="container">
        <h3
          className="text-md-start fw-bolder mb-2 text-link text-black mt-3"
          onClick={() => navigate("/")}
        >
          <Logo />
        </h3>

        <h4 className="fw-bolder">Create a developer account</h4>

        <p className="text-light-black fs-14 mb-4">
          Complete your developer account setup
        </p>

        {!state && (
          <WarningDisplay
            message="We couldn't find your registration information. Please go back to complete your registration."
            onClose={() => navigate("/get-started/developer")}
          />
        )}

        <button
          type="button"
          onClick={() => navigate(-1)}
          className="btn btn-link text-decoration-none text-primary fw-bold mb-3 px-0"
        >
          ← Previous
        </button>

        <form onSubmit={handleSubmit} className={!state ? "opacity-50" : ""}>
          <div>
            <ImageUpload
              title="Enter your company image"
              onImageUpload={handleImageUpload}
            />
          </div>
          <div className="col mb-3">
            <InputComponent
              label="Company Name"
              placeholder="Enter company name"
              icon="name"
              iconType="fa"
              type="text"
              value={companyName}
              change={(e) => {
                setCompanyName(e.target.value);
              }}
            />
          </div>
          <div className="col mb-3">
            <InputComponent
              label="Company Address"
              placeholder="Enter company address"
              icon="address"
              iconType="fa"
              type="text"
              value={companyAddress}
              change={(e) => {
                setCompanyAddress(e.target.value);
              }}
            />
          </div>
          <div className="col mb-3">
            <InputComponent
              label="Company Registration Number (CRN) / CAC Number"
              placeholder="Enter number"
              icon="id-badge"
              iconType="fa"
              type="text"
              value={taxId}
              change={(e) => {
                setTaxId(e.target.value);
              }}
              required
            />
          </div>
          <div className="col mb-3">
            <InputComponent
              label="Link to company website"
              placeholder="Enter link"
              icon="link"
              iconType="fa"
              type="text"
              value={websiteLink}
              change={(e) => {
                setWebsiteLink(e.target.value);
              }}
            />
          </div>
          <div className="col mb-3">
            <InputComponent
              type="text"
              label="Bio(Company details)"
              placeholder="Enter your company history"
              iconType="fa"
              value={details}
              change={(e) => setDetails(e.target.value)}
              required
              isTextArea={true}
              rows={6}
            />
          </div>
          <div className="col mb-3">
            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                name="terms"
                checked={agree}
                id="terms"
                onChange={() => setAgree(!agree)}
                required
              />
              <label className="form-check-label fs-14 fw-400" htmlFor="terms">
                By signing up you agree to our{" "}
                <a href="/terms" className="text-primary fw-bold">
                  Terms of service
                </a>{" "}
                and{" "}
                <a href="/privacy" className="text-primary fw-bold">
                  Privacy policy
                </a>
              </label>
            </div>
          </div>

          <ErrorDisplay errors={error} onClose={() => setError([])} />
          <SuccessDisplay message={success} />

          <div className="d-grid">
            <PrimaryButton
              type="primary"
              text="Create account"
              loading={loading}
              disable={!state}
            />
          </div>
        </form>
        <p className="text-light-black text-center my-4 my-md-3 fs-14">
          Already have an account?{" "}
          <Link className="text-link" to="/sign-in">
            Sign In
          </Link>
        </p>
      </div>
    </OnboardingLayout>
  );
};

export default DeveloperUpload;
